import { useNavigate } from 'react-router-dom';
import { SubscriptionEvent, SubscriptionMetrics } from './adminApi';
import { fmtAdminDateTime } from './timeFormat';
import './Admin.css';

/** Timeline names of `subscriptionEvents` (see admin.py). */
export const SUBSCRIPTION_EVENT_LABELS: Record<string, string> = {
  subscription_cancellation_scheduled: 'Cancellation scheduled',
  subscription_cancellation_undone: 'Cancellation undone',
  subscription_cancellation_feedback: 'Cancellation feedback',
  subscription_plan_changed: 'Plan changed',
  subscription_ended: 'Subscription ended',
  subscription_payment_failed: 'Payment failed',
};

export function subscriptionEventLabel(type: string | null | undefined): string {
  const name = type?.startsWith('subscription_') ? type : `subscription_${type}`;
  return SUBSCRIPTION_EVENT_LABELS[name] || type || 'event';
}

const BADGE: Record<string, string> = {
  cancellation_scheduled: 'partial',
  cancellation_undone: 'positive',
  cancellation_feedback: 'neutral',
  plan_changed: 'neutral',
  subscription_ended: 'negative',
  payment_failed: 'negative',
};

const ZERO_DECIMAL = new Set(['jpy', 'krw', 'vnd']);

function fmtAmount(amount: number | null | undefined, currency: string | null | undefined): string | null {
  if (amount == null || !currency) return null;
  const value = ZERO_DECIMAL.has(currency.toLowerCase()) ? amount : amount / 100;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() }).format(value);
  } catch {
    return `${value} ${currency}`;
  }
}

const plan = (name?: string | null, interval?: string | null) =>
  [name, interval].filter(Boolean).join(' / ') || '?';

/** One-line description of what happened, e.g. "callout_10 / month → callout_20 / month (upgrade)". */
export function describeSubscriptionEvent(e: SubscriptionEvent): string[] {
  const parts: string[] = [];
  if (e.type === 'plan_changed' || e.planChange) {
    parts.push(
      `${plan(e.previousPlanName, e.previousBillingInterval)} → ${plan(e.planName, e.billingInterval)}` +
        (e.planChange ? ` (${e.planChange.replace('_', ' ')})` : '')
    );
  } else if (e.planName) {
    parts.push(plan(e.planName, e.billingInterval));
  }
  if (e.type === 'cancellation_scheduled' && e.cancelAt) parts.push(`ends ${fmtAdminDateTime(e.cancelAt)}`);
  if (e.type === 'subscription_ended' && e.replaced) parts.push('replaced by another subscription');
  if (e.type === 'payment_failed') {
    const amount = fmtAmount(e.amountDue, e.currency);
    if (amount) parts.push(amount);
    if (e.attemptCount) parts.push(`attempt ${e.attemptCount}`);
  }
  if (e.cancellationReason) parts.push(`reason: ${e.cancellationReason.replace(/_/g, ' ')}`);
  if (e.cancellationFeedback) parts.push(`feedback: ${e.cancellationFeedback.replace(/_/g, ' ')}`);
  return parts;
}

export function SubscriptionEventRow({ e }: { e: SubscriptionEvent }) {
  return (
    <div className="admin-call" style={{ padding: '10px 14px' }}>
      <div className="admin-call-head">
        <div>
          <span className={`answer-badge ${BADGE[e.type] || 'neutral'}`}>{subscriptionEventLabel(e.type)}</span>
        </div>
        <div className="admin-call-meta">
          <span>{fmtAdminDateTime(e.eventCreatedAt)}</span>
          {describeSubscriptionEvent(e).map((part) => (
            <span key={part}>{part}</span>
          ))}
        </div>
      </div>
      {e.cancellationComment && <div className="admin-prompt">{e.cancellationComment}</div>}
    </div>
  );
}

function Card({ value, label }: { value: number | string; label: string }) {
  return (
    <div className="admin-card">
      <div className="value">{value}</div>
      <div className="label">{label}</div>
    </div>
  );
}

/** Subscription cards, warnings and recent events for the Analytics tab. */
export function SubscriptionSummary({ metrics }: { metrics: SubscriptionMetrics }) {
  const navigate = useNavigate();
  return (
    <div className="admin-section">
      <h2>Subscriptions</h2>
      <div className="admin-muted" style={{ marginBottom: 12, fontSize: 13 }}>
        Counts users, without {metrics.excludedAdminAccounts} admin account
        {metrics.excludedAdminAccounts === 1 ? '' : 's'}
        {metrics.adminSubscribers > 0 && ` (${metrics.adminSubscribers} on a paid plan)`}. Cancellations,
        plan changes and failed payments are recorded from Stripe webhooks; earlier ones aren't known.
      </div>
      <div className="admin-cards">
        <Card value={metrics.activeSubscribers} label="Active subscribers" />
        <Card value={metrics.cancellationsScheduled} label="Cancellations scheduled" />
        <Card value={`${metrics.churned30d} / ${metrics.churned90d}`} label="Churned (30 / 90 days)" />
        <Card
          value={`${metrics.failedPayments30d} / ${metrics.failedPayments90d}`}
          label="Failed payments (30 / 90 days)"
        />
        <Card value={metrics.planChanges90d} label="Plan changes (90 days)" />
        <Card value={metrics.payingUsers} label="Paying users (ever)" />
      </div>

      {metrics.unmatchedEvents > 0 && (
        <div className="admin-notice">
          {metrics.unmatchedEvents} Stripe event{metrics.unmatchedEvents === 1 ? '' : 's'} matched no user
          profile (no <code>stripeCustomerId</code>), so no profile was updated. Search the payment backend logs
          for <code>UNMATCHED</code>.
        </div>
      )}
      {metrics.possiblyLapsed.length > 0 && (
        <div className="admin-notice">
          On a paid plan with no checkout or renewal for over a billing period (a missed cancellation?):{' '}
          {metrics.possiblyLapsed.map((u, i) => (
            <span key={u.uid}>
              {i > 0 && ', '}
              <a
                href={`/admin/users/${u.uid}`}
                onClick={(ev) => {
                  ev.preventDefault();
                  navigate(`/admin/users/${u.uid}`);
                }}
              >
                {u.email || u.uid}
              </a>{' '}
              ({plan(u.plan, u.billingInterval)}, last paid {u.lastPaymentAt ? fmtAdminDateTime(u.lastPaymentAt) : 'never'}
              {!u.hasStripeCustomer && ', no Stripe customer'})
            </span>
          ))}
        </div>
      )}

      {metrics.recentEvents.length > 0 && (
        <div className="admin-table-wrap" style={{ marginTop: 12 }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>When</th>
                <th>User</th>
                <th>Event</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {metrics.recentEvents.map((e, i) => (
                <tr key={i} onClick={() => e.userId && navigate(`/admin/users/${e.userId}`)}>
                  <td>{fmtAdminDateTime(e.at)}</td>
                  <td>{e.email || e.userId || <span className="admin-muted">unmatched</span>}</td>
                  <td>
                    <span className={`answer-badge ${BADGE[e.type] || 'neutral'}`}>{subscriptionEventLabel(e.type)}</span>
                  </td>
                  <td className="wrap">
                    {[...describeSubscriptionEvent(e), e.cancellationComment && `“${e.cancellationComment}”`]
                      .filter(Boolean)
                      .join(' · ')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
